const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const MONGO_OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

/** Synthetic tree ids for feed-only supplement rows (not valid gallery routes). */
export const SUPPLEMENT_ROW_ID_PREFIX = 'supplement:';

export const isLayoutUuid = (id) =>
  typeof id === 'string' && UUID_RE.test(id.trim());

export const isMongoObjectId = (id) =>
  typeof id === 'string' && MONGO_OBJECT_ID_RE.test(id.trim());

/**
 * Resolve a plan row to a floor-gallery URL. Prefers an explicit galleryUrl
 * from the feed; otherwise builds /floor-gallery/{mongoId} for real ObjectIds.
 * UUID-only or supplement:* ids cannot be opened via a constructed path.
 */
export function resolveFloorGalleryUrl(plan, origin = '') {
  if (!plan) return null;
  const galleryUrl =
    typeof plan.galleryUrl === 'string' ? plan.galleryUrl.trim() : '';
  if (galleryUrl && /^https?:\/\//i.test(galleryUrl)) return galleryUrl;

  const id = plan._id ? String(plan._id) : '';
  if (!id || id.startsWith(SUPPLEMENT_ROW_ID_PREFIX) || !isMongoObjectId(id)) {
    return null;
  }
  const base =
    origin ||
    (typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : '');
  return base ? `${base}/floor-gallery/${id}` : null;
}

export const isRealCustomerRef = (rawRef) => {
  if (!rawRef || typeof rawRef !== 'string') return false;
  const s = rawRef.trim();
  if (!s || s.includes('not-provided')) return false;
  if (s.startsWith('http')) return false;
  return true;
};

/** Layout UUID required by POST /listing-layout-links (SavedLayout.uuid). */
export const extractLayoutUuidFromGroup = (group) => {
  if (!group?.rows) return null;
  const ready = group.rows.find((r) => r?.meta?.event === 'gallery_ready');
  if (ready?.detail?.uniqueId && isLayoutUuid(ready.detail.uniqueId)) {
    return ready.detail.uniqueId.trim();
  }
  for (const r of group.rows) {
    const uid = r?.detail?.uniqueId;
    if (uid && isLayoutUuid(uid)) return uid.trim();
  }
  const baseKey = String(group.key || '').includes('_split_')
    ? group.key.split('_split_')[0]
    : group.key;
  if (isLayoutUuid(baseKey)) return baseKey.trim();
  return null;
};

/** Map layoutUuid → customer_ref from GET /listing-layout-links. */
export const layoutCustomerRefByUuid = (links) => {
  const map = new Map();
  (Array.isArray(links) ? links : []).forEach((link) => {
    const uuid = link?.layoutUuid;
    const ref = link?.customer_ref;
    if (uuid && isRealCustomerRef(ref)) map.set(uuid, ref.trim());
  });
  return map;
};

/** Map layoutUuid → link record (for disconnect). */
export const layoutLinkByUuid = (links) => {
  const map = new Map();
  (Array.isArray(links) ? links : []).forEach((link) => {
    const uuid = link?.layoutUuid;
    if (uuid && link?._id) map.set(uuid, link);
    if (uuid && link?.id) map.set(uuid, { ...link, _id: link.id });
  });
  return map;
};
